import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { equipService } from '../services/api';

/**
 * Equips amb codi d'accés imprès (instructor).
 *
 * Es fan servir dues vegades i prou: es creen a principi de curs i s'imprimeixen
 * les targetes. Les missions s'assignen a part, amb el gestor d'assignacions de
 * la pantalla de missions —un equip és un analista corrent—, i per això aquí es
 * mostra què té assignat cadascú: a l'examen no tots els equips fan la mateixa
 * missió, i abans d'imprimir val la pena veure-ho d'un cop d'ull.
 *
 * La impressió és una vista a part (`@media print` a App.css): del full que en
 * surt només hi ha les targetes, una per equip, retallables.
 */
function Equips() {
  const { t } = useTranslation();
  const [equips, setEquips] = useState([]);
  const [carregant, setCarregant] = useState(true);
  const [quantitat, setQuantitat] = useState(12);
  const [creant, setCreant] = useState(false);

  useEffect(() => {
    carrega();
  }, []);

  const carrega = async () => {
    setCarregant(true);
    try {
      const response = await equipService.getEquips();
      setEquips(response.data);
    } catch (error) {
      toast.error(t('equips.errorCarregant'));
    } finally {
      setCarregant(false);
    }
  };

  const crea = async () => {
    setCreant(true);
    try {
      const response = await equipService.creaEquips(Number(quantitat));
      toast.success(t('equips.creatsOk', { nombre: response.data.length }));
      await carrega();
    } catch (error) {
      const detall = error.response?.data?.detail;
      toast.error(typeof detall === 'string' ? detall : t('equips.errorCreant'));
    } finally {
      setCreant(false);
    }
  };

  const regenera = async (equip) => {
    if (!window.confirm(t('equips.confirmaRegenerar', { equip: equip.display_name }))) return;
    try {
      await equipService.regeneraCodi(equip.id);
      toast.success(t('equips.codiRegenerat'));
      await carrega();
    } catch (error) {
      toast.error(t('equips.errorCodi'));
    }
  };

  const revoca = async (equip) => {
    if (!window.confirm(t('equips.confirmaRevocar', { equip: equip.display_name }))) return;
    try {
      await equipService.revocaCodi(equip.id);
      toast.success(t('equips.codiRevocat'));
      await carrega();
    } catch (error) {
      toast.error(t('equips.errorCodi'));
    }
  };

  /**
   * Eliminar un equip.
   *
   * Dues finestres quan hi ha entregues, i no és per fer-ho pesat: el botó és a
   * la mateixa fila que el de regenerar el codi, i la diferència entre «targeta
   * nova» i «fora l'equip i la seva feina» no es pot deixar en una sola premuda.
   * El servidor no se'n fia tampoc: sense `amb_entregues` respon 409.
   */
  const elimina = async (equip) => {
    if (!window.confirm(t('equips.confirmaEliminar', { equip: equip.display_name }))) return;
    try {
      await equipService.elimina(equip.id);
      toast.success(t('equips.eliminat', { equip: equip.display_name }));
      await carrega();
      return;
    } catch (error) {
      if (error.response?.status !== 409) {
        toast.error(t('equips.errorEliminar'));
        return;
      }
      // El servidor diu quantes entregues es perdrien. El número ha de sortir a
      // la pregunta: «té feina» no fa pensar el mateix que «té 3 entregues».
      const detall = error.response?.data?.detail;
      if (!window.confirm(`${detall}\n\n${t('equips.confirmaEliminarEntregues')}`)) return;
      try {
        await equipService.elimina(equip.id, true);
        toast.success(t('equips.eliminat', { equip: equip.display_name }));
        await carrega();
      } catch (segon) {
        toast.error(t('equips.errorEliminar'));
      }
    }
  };

  const tancaSessio = async (equip) => {
    if (!window.confirm(t('equips.confirmaTancarSessio', { equip: equip.display_name }))) return;
    try {
      await equipService.tancaSessio(equip.id);
      toast.success(t('equips.sessioTancada'));
      await carrega();
    } catch (error) {
      toast.error(t('equips.errorSessio'));
    }
  };

  if (carregant) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="equips-pagina">
      <div className="no-imprimir">
        <h1>{t('equips.titol')}</h1>
        <p className="equips-intro">{t('equips.intro')}</p>
      </div>

      <div className="card no-imprimir">
        <div className="equips-crear">
          <label htmlFor="quantitat-equips">{t('equips.quantsEquips')}</label>
          <input
            id="quantitat-equips"
            type="number"
            min="1"
            max="100"
            value={quantitat}
            onChange={(e) => setQuantitat(e.target.value)}
          />
          <button className="btn btn-primary" onClick={crea} disabled={creant}>
            {creant ? t('equips.creant') : t('equips.creaBoto')}
          </button>
        </div>
        <p className="form-help">{t('equips.creaAjuda')}</p>
      </div>

      {equips.length === 0 ? (
        <div className="empty-state no-imprimir">
          <p>{t('equips.capEquip')}</p>
        </div>
      ) : (
        <>
          <div className="equips-accions no-imprimir">
            <button className="btn btn-secondary" onClick={() => window.print()}>
              {t('equips.imprimeix')}
            </button>
          </div>

          <div className="card no-imprimir">
            <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>{t('equips.equip')}</th>
                  <th>{t('equips.codi')}</th>
                  <th>{t('equips.missions')}</th>
                  <th>{t('equips.estat')}</th>
                  <th>{t('equips.accions')}</th>
                </tr>
              </thead>
              <tbody>
                {equips.map((equip) => (
                  <tr key={equip.id}>
                    <td>{equip.display_name}</td>
                    <td>
                      {equip.codi
                        ? <code className="codi-acces">{equip.codi}</code>
                        : <span className="text-muted">{t('equips.senseCodi')}</span>}
                    </td>
                    <td>
                      {equip.missions.length === 0
                        ? <span className="text-muted">{t('equips.senseMissio')}</span>
                        : equip.missions.join(', ')}
                    </td>
                    <td>
                      {equip.sessio_oberta
                        ? <span className="badge badge-success">{t('equips.sessioOberta')}</span>
                        : <span className="text-muted">—</span>}
                      {equip.entregues > 0 && (
                        <span className="badge badge-primary" style={{ marginLeft: 'var(--spacing-sm)' }}>
                          {t('equips.entregues', { nombre: equip.entregues })}
                        </span>
                      )}
                    </td>
                    <td className="equips-fila-accions">
                      <button className="btn btn-secondary btn-sm" onClick={() => regenera(equip)}>
                        {t('equips.codiNou')}
                      </button>
                      {/* Revocar és l'acció de després de l'examen: tanca la
                          porta i conserva la feina. Per això va abans
                          d'eliminar i amb el mateix pes visual que la resta. */}
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => revoca(equip)}
                        disabled={!equip.codi}
                        title={t('equips.revocaAjuda')}
                      >
                        {t('equips.revoca')}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => tancaSessio(equip)}
                        disabled={!equip.sessio_oberta}
                      >
                        {t('equips.tancaSessio')}
                      </button>
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => elimina(equip)}
                        title={t('equips.eliminaAjuda')}
                      >
                        {t('equips.elimina')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* El full que s'imprimeix. A pantalla no es veu; en imprimir, és
              l'única cosa que surt. Cada targeta porta el nom de l'equip, el
              codi i on s'ha d'anar: sense l'adreça, la targeta no serveix de res
              a qui la rep. */}
          <div className="equips-targetes">
            {equips.filter(e => e.codi).map((equip) => (
              <div className="targeta-equip" key={equip.id}>
                <div className="targeta-equip__nom">{equip.display_name}</div>
                <div className="targeta-equip__codi">{equip.codi}</div>
                <div className="targeta-equip__peu">
                  <div className="targeta-equip__adreca">ach.strategos.cat</div>
                  <div className="targeta-equip__instruccio">{t('equips.targetaInstruccio')}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Equips;
